import type { ClassKind } from "./enums.ts";
import { balanceFor, packageProgress } from "./member-balances.ts";
import type { CreditPurchase } from "./schemas/billing.ts";
import type { Participant } from "./schemas/identity.ts";
import { localDate, MONTH_SHORT, WEEK_ORDER } from "./time.ts";
import { membershipBlock } from "./membership.ts";
import { packagePaysFor, type PaidClass } from "./open-classes.ts";

/**
 * The family's picks on the Book tab (Asana "Book the whole month") as data: the bar's words and
 * progress, and how the server pays each pick from a package. The week, the session cards and the
 * "every week" offer are in `book-week.ts` (round 3, approved 2026-10-18). Both screens, the web and
 * the phone, use these.
 *
 * Day keys are `yyyy-MM-dd` in the business's timezone, times are `HH:mm` on its wall clock.
 */

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEKDAY_PLURAL = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

/** The weekday of a day key, 0 for Sunday. */
export function weekdayOfKey(key: string): number {
  return new Date(`${key}T00:00:00.000Z`).getUTCDay();
}

/** "Mon 19 Oct" */
export function shortDay(key: string): string {
  const date = new Date(`${key}T00:00:00.000Z`);
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]}`;
}

export function weekdayShort(weekday: number): string {
  return WEEKDAY_SHORT[weekday] ?? "";
}

export function weekdayLong(weekday: number): string {
  return WEEKDAY_LONG[weekday] ?? "";
}

/** "Mon, Thu and Sat" */
export function andList(items: string[]): string {
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The bar's head: "7 picked, 1 left", or per person when more than one has picks: "Lina 4, Zaid 1,
 * 0 left".
 */
export function picksHead(perPerson: ReadonlyArray<{ name: string; count: number }>, free: number): string {
  const withPicks = perPerson.filter((row) => row.count > 0);
  const total = withPicks.reduce((sum, row) => sum + row.count, 0);
  const head = withPicks.length > 1 ? withPicks.map((row) => `${row.name} ${row.count}`).join(", ") : `${total} picked`;
  return `${head}, ${free} left`;
}

/**
 * The bar under the head: one segment a session of the package, booked first, then the picks of
 * each person, then what is left. More than `MAX_SEGMENTS` sessions draw as one bar in proportion.
 */
export const MAX_SEGMENTS = 40;

export type ProgressPart = { kind: "booked" | "picked" | "left"; count: number; person?: number };

export function progressParts(input: { booked: number; picks: readonly number[]; left: number }): ProgressPart[] {
  const parts: ProgressPart[] = [];
  if (input.booked > 0) parts.push({ kind: "booked", count: input.booked });
  input.picks.forEach((count, person) => {
    if (count > 0) parts.push({ kind: "picked", count, person });
  });
  if (input.left > 0) parts.push({ kind: "left", count: input.left });
  return parts;
}

/** A package as the picks see it: who it belongs to, what it pays for, until when, and what is left. */
export type PayingPackage = {
  id: string;
  /** The family account (`userId`). */
  account: string;
  kind: "group" | "private";
  /** Null pays for every class of its kind. */
  groupIds: readonly string[] | null;
  /** ISO instant. A session that starts at or after it is not paid for. */
  expiresAt: string;
  left: number;
  /** The business recorded this period as not paid. It pays for nothing unless the business allows it. */
  owed?: boolean;
  /**
   * A month of a membership (M2): it pays only for a session that starts inside the month
   * (`startsAt` to `expiresAt`), it pays before a pack, and it never pays while owed, by the shared
   * cap rule (`membershipBlock`).
   */
  membership?: boolean;
  /** ISO instant: the first instant of a membership month. */
  startsAt?: string | null;
  /** A membership month with no cap: `left` is not read. */
  unlimited?: boolean;
};

/** A pick as the packages see it. `openToAll`: its class is open to all levels (`packagePaysFor`). */
export type PayingPick = { account: string; kind: "group" | "private"; groupId: string; openToAll?: boolean; startsAt: string };

/**
 * Pays each pick from a package, the way the server books a batch: picks in time order, each from
 * the soonest-ending package of the same family that pays for its class and kind and still runs when
 * the session starts (`usablePurchases`, `pickPurchaseForBooking`). Answers, for each pick in the
 * order given, whether a package paid for it. The screen uses it so it never offers a date the
 * server would refuse for want of a session.
 */
export function payPicks(
  packages: readonly PayingPackage[],
  picks: readonly PayingPick[],
  /** The business setting "allow booking while owed". Off by default, as on the server. */
  options: { allowOwed?: boolean } = {},
): boolean[] {
  const left = new Map(packages.map((row) => [row.id, row.left]));
  // A membership month pays first while it has a class left, as on the server (`usablePurchases`).
  const bySoonest = [...packages].sort(
    (a, b) => Number(!a.membership) - Number(!b.membership) || a.expiresAt.localeCompare(b.expiresAt) || a.id.localeCompare(b.id),
  );
  const order = picks.map((pick, index) => ({ pick, index })).sort((a, b) => a.pick.startsAt.localeCompare(b.pick.startsAt) || a.index - b.index);
  const paid = picks.map(() => false);
  for (const { pick, index } of order) {
    const startsAt = new Date(pick.startsAt).getTime();
    const chosen = bySoonest.find(
      (row) =>
        row.account === pick.account &&
        row.kind === pick.kind &&
        packagePaysFor(row.groupIds, { id: pick.groupId, openToAll: pick.openToAll }) &&
        (row.membership
          ? membershipBlock(
              { periodStartsAt: row.startsAt ?? row.expiresAt, expiresAt: row.expiresAt, unlimited: row.unlimited ?? false, left: left.get(row.id) ?? 0, paid: !row.owed },
              pick.startsAt,
            ) === null
          : (options.allowOwed || !row.owed) &&
            new Date(row.expiresAt).getTime() > startsAt &&
            // A package that waits for another one pays from its first day (migration 0085).
            (!row.startsAt || new Date(row.startsAt).getTime() <= startsAt) &&
            (left.get(row.id) ?? 0) > 0),
    );
    if (!chosen) continue;
    if (!chosen.unlimited) left.set(chosen.id, (left.get(chosen.id) ?? 0) - 1);
    paid[index] = true;
  }
  return paid;
}

/** Whether every pick, with `extra` added, can be paid. */
export function allPaid(
  packages: readonly PayingPackage[],
  picks: readonly PayingPick[],
  options: { allowOwed?: boolean } = {},
): boolean {
  return payPicks(packages, picks, options).every(Boolean);
}

// --- The screen helpers (moved from the Desk web Book screen on 2026-10-17, for the phone) ------

/** "Me" for the account holder, else the person's name. */
export function personLabel(participant: Pick<Participant, "kind" | "displayName">): string {
  return participant.kind === "self" ? "Me" : participant.displayName;
}

/** "Monday 12 October" from a day key. */
export function longDay(key: string): string {
  const date = new Date(`${key}T00:00:00.000Z`);
  return `${WEEKDAY_LONG[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_LONG[date.getUTCMonth()]}`;
}

const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "4:45 PM" from a wall-clock `HH:mm`. */
export function timeLabel(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const half = hours! >= 12 ? "PM" : "AM";
  return `${hours! % 12 === 0 ? 12 : hours! % 12}:${String(minutes).padStart(2, "0")} ${half}`;
}

/** The purchases that carry sessions at a business now, of any family account the member sees. */
export function livePurchases(purchases: readonly CreditPurchase[], businessId: string, now: Date = new Date()): CreditPurchase[] {
  return purchases.filter(
    (purchase) =>
      purchase.businessId === businessId && purchase.status === "active" && new Date(purchase.expiresAt).getTime() > now.getTime(),
  );
}

/**
 * The live purchases as `payPicks` reads them. An owed period pays for nothing here: a member cannot
 * read the business's "allow booking while owed" setting, so the screen follows the default, like
 * the session page. The server still decides.
 */
export function payingPackages(purchases: readonly CreditPurchase[], businessId: string, now: Date = new Date()): PayingPackage[] {
  return livePurchases(purchases, businessId, now).map((purchase) => ({
    owed: purchase.paymentStatus === "owed",
    membership: Boolean(purchase.membershipId),
    startsAt: purchase.periodStartsAt ?? null,
    unlimited: purchase.unlimited ?? false,
    id: purchase.id,
    account: purchase.userId,
    kind: purchase.productKind,
    groupIds: purchase.groupIds,
    expiresAt: purchase.expiresAt,
    left: Math.max(0, purchase.creditsRemaining),
  }));
}

/** The sessions left of the family accounts in the picks, after every pick. Owed sessions are not left. */
export function familyFree(
  purchases: CreditPurchase[],
  businessId: string | null,
  accounts: readonly string[],
  picks: ReadonlyArray<{ account: string }>,
): number {
  if (!businessId) return 0;
  const left = accounts.reduce((sum, owner) => {
    const balance = balanceFor(purchases, businessId, owner);
    return sum + balance.left - balance.owed;
  }, 0);
  return left - picks.filter((pick) => accounts.includes(pick.account)).length;
}

/**
 * The last day a family's packages pay for (a kind and a class, or any when null): the latest expiry
 * of a package with sessions left, or of any live package once every session is used. Null when
 * there is none.
 */
export function packageEnd(
  packages: readonly PayingPackage[],
  account: string,
  kind: ClassKind | null,
  /** The class, or null for any. An open class is paid only by a package that names it. */
  group: PaidClass | null,
  timezone: string,
): string | null {
  const all = packages.filter(
    (row) =>
      row.account === account &&
      (kind === null || row.kind === kind) &&
      (group === null || packagePaysFor(row.groupIds, group)),
  );
  const withSessions = all.filter((row) => row.left > 0 || (row.unlimited && !row.owed));
  const live = withSessions.length > 0 ? withSessions : all;
  if (live.length === 0) return null;
  const last = Math.max(...live.map((row) => new Date(row.expiresAt).getTime()));
  // A session that starts at the expiry is not paid for, so the last day is the minute before it.
  return localDate(new Date(last - 60_000), timezone);
}

/**
 * The progress of the family's packages: used, each person's picks (in `people` order), left. Each
 * screen draws `parts` with its own colours.
 */
export function familyProgressCounts(input: {
  purchases: readonly CreditPurchase[];
  businessId: string | null;
  accounts: readonly string[];
  people: ReadonlyArray<{ id: string }>;
  picks: ReadonlyArray<{ participantId: string; account: string }>;
}): {
  total: number;
  used: number;
  perPerson: number[];
  pickedCount: number;
  left: number;
  parts: ProgressPart[];
  /** One segment a session, or one bar in proportion when there are too many. */
  segments: boolean;
  label: string;
} {
  const { businessId, accounts, people, picks } = input;
  const live = businessId
    ? livePurchases(input.purchases, businessId).filter((purchase) => accounts.includes(purchase.userId) && purchase.paymentStatus !== "owed")
    : [];
  const total = live.reduce((sum, purchase) => sum + packageProgress(purchase).total, 0);
  const used = live.reduce((sum, purchase) => sum + packageProgress(purchase).used, 0);
  const perPerson = people.map((person) => picks.filter((pick) => pick.participantId === person.id && accounts.includes(pick.account)).length);
  const pickedCount = perPerson.reduce((sum, count) => sum + count, 0);
  const left = Math.max(0, total - used - pickedCount);
  return {
    total,
    used,
    perPerson,
    pickedCount,
    left,
    parts: progressParts({ booked: used, picks: perPerson, left }),
    segments: total <= MAX_SEGMENTS,
    label: `${pickedCount} picked, ${left} left of ${total} sessions${used > 0 ? `, ${used} booked` : ""}`,
  };
}
